import React, { CSSProperties, FC, useEffect, useMemo, useRef, useCallback, useState, Children } from 'react';
import { useDrag, DragSourceMonitor, useDrop, DropTargetMonitor } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import {
  IDocumentsDndItem,
  IDocumentsTableProps,
  isDocumentsDndItem,
  isUploadDndItem,
  IUploadDndItem
} from '../../types';
import { DocsNodeType } from 'src/models/documentsLibrary/types';
import styles from './index.module.scss';
import classNames from 'classnames';
import {
  useUpdateFolder,
  useUpdateFile,
  getUpdateCacheOnFileMove,
  getUpdateCacheOnFolderMove,
  useCurrentFolderContext,
  useLoadDocuments,
  useActiveDocNode,
  useUploadFile
} from 'src/models/documentsLibrary/hooks';
import notification from 'src/common/notification';
import { useTranslation } from 'react-i18next';
import SingleLineSkeleton from 'src/common/betaComponents/SingleLineSkeleton';
import { NativeTypes } from 'react-dnd-html5-backend';
import DocumentsUploadView from '../../DocumentsView/UploadView';
import { DndType } from 'src/types/dnd';

interface Props {
  record: IDocumentsTableProps;
  className?: string;
  style?: CSSProperties;
  [x: string]: unknown;
}

export const DocumentsDragRow: FC<Props> = ({ record, moveRow, className, style, children, ...restProps }) => {
  const { nodeType, id, name } = record;
  const { t } = useTranslation();
  const [moveFolder, { loading: moveFolderLoading }] = useUpdateFolder();
  const [moveFile, { loading: moveFileLoading }] = useUpdateFile();
  const currentFolder = useCurrentFolderContext();
  const { refetch } = useLoadDocuments(null);
  const [dropItem, setDropItem] = useState<string>('');
  const [activeDocNode, updateActiveDocNode] = useActiveDocNode();
  const isUpdating = useMemo(() => activeDocNode === id, [activeDocNode, id]);
  const [uploadFile] = useUploadFile();

  const isLoading = moveFolderLoading || moveFileLoading;
  const dropableRef = useRef<HTMLTableRowElement>(null);

  const dndDocumentsItem: IDocumentsDndItem = useMemo(() => {
    return {
      type: DndType.DocumentsItem,
      docsNode: record
    };
  }, [record]);

  const [{ opacity }, drag, preview] = useDrag({
    item: dndDocumentsItem,
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
      item: monitor.getItem(),
      opacity: monitor.isDragging() ? 0.4 : 1
    })
  });

  const onDrop = useCallback(
    (item: IDocumentsDndItem | IUploadDndItem, monitor: DropTargetMonitor) => {
      const didDrop = monitor.didDrop();
      if (didDrop) {
        return;
      }
      if (isDocumentsDndItem(item)) {
        const { docsNode } = item;
        const { nodeType: itemNodeType, id: itemId, name: itemName } = docsNode;
        setDropItem(itemId);
        if (itemNodeType === DocsNodeType.FOLDER) {
          moveFolder({
            variables: { id: itemId, parentId: id, name: itemName },
            update: getUpdateCacheOnFolderMove(itemId, currentFolder.id, id)
          }).catch(err => {
            // TODO: to find a more gracious way of solving this issue
            // same bug(cache and nullable variable in query): https://github.com/apollographql/apollo-client/issues/2051
            const lsRootQueryErr = `Can't find field 'ls' on ROOT_QUERY object`;
            if (
              typeof err.message === 'string' &&
              (err.message === lsRootQueryErr || err.message.includes(lsRootQueryErr))
            ) {
              refetch();
            } else {
              notification.error({
                description: t('Common.unknownErrorDesc'),
                message: t('Common.unknownError')
              });
            }
          });
        } else {
          moveFile({
            variables: { id: itemId, parentId: id, name: itemName },
            update: getUpdateCacheOnFileMove(itemId, currentFolder.id, id)
          }).catch(err => {
            const lsRootQueryErr = `Can't find field 'ls' on ROOT_QUERY object`;
            if (
              typeof err.message === 'string' &&
              (err.message === lsRootQueryErr || err.message.includes(lsRootQueryErr))
            ) {
              refetch();
            } else {
              notification.error({
                description: t('Common.unknownErrorDesc'),
                message: t('Common.unknownError')
              });
            }
          });
        }
      } else if (isUploadDndItem(item)) {
        const { files } = item;
        const file = files[0];
        uploadFile(file, id);
      } else {
        return;
      }
    },
    [currentFolder.id, id, moveFile, moveFolder, refetch, t, uploadFile]
  );

  const [{ isOver, isOverCurrent, canDrop }, drop] = useDrop({
    accept: [DndType.DocumentsItem, NativeTypes.FILE],
    canDrop(item: IDocumentsDndItem | IUploadDndItem) {
      if (isDocumentsDndItem(item)) {
        return nodeType !== DocsNodeType.FILE && item.docsNode.id !== id && !isUpdating;
      } else if (isUploadDndItem(item)) {
        return nodeType !== DocsNodeType.FILE && !isUpdating;
      } else {
        return false;
      }
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
      item: monitor.getItem(),
      getItemType: monitor.getItemType(),
      isOverCurrent: monitor.isOver({ shallow: true }) && nodeType !== DocsNodeType.FILE
    }),
    drop: onDrop
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  drag(drop(dropableRef));

  const isDropOver = useMemo(() => {
    return canDrop && isOver;
  }, [canDrop, isOver]);

  useEffect(() => {
    updateActiveDocNode?.(isLoading ? dropItem : null);
  }, [dropItem, isLoading, updateActiveDocNode]);

  return (
    <tr
      ref={dropableRef}
      className={classNames(
        { [styles.isDropOver]: isDropOver, [styles.isOverCurrent]: isOverCurrent, [styles.isDropable]: canDrop },
        styles.row,
        className
      )}
      style={{ cursor: 'move', opacity, ...style }}
      {...restProps}
    >
      {isUpdating
        ? [
            ...(children as (typeof Children)[]),
            <td className={styles.loadingCol} key={`loadingCol`}>
              <SingleLineSkeleton active loading />
            </td>
          ]
        : [
            children as typeof Children,
            isOverCurrent && canDrop && (
              <td className={styles.uploadCol} style={{ padding: 0 }} key={`uploadCol`}>
                <DocumentsUploadView name={name} />
              </td>
            )
          ]}
    </tr>
  );
};

const RowComponent: FC<Props> = props => {
  const { record } = props;

  if (record) {
    return <DocumentsDragRow {...props} />;
  } else {
    return <tr {...props} />;
  }
};

export default RowComponent;
